import React, { useState, useEffect } from 'react';
import { briefingComponentRegistry } from './briefingComponentRegistry';
import mockBriefing from './mock_briefing.json';
import { useTheme } from '../../contexts/ThemeContext';
import { masterSupabase } from '../../lib/supabase-master.js';

// ── Tagesübersicht-Kachel ─────────────────────────────────────────────────────
const SummaryTile = ({ icon, count, label, color }) => {
  const colorMap = {
    red:    'bg-red-950/40 border-red-800/50 text-red-400',
    yellow: 'bg-yellow-950/40 border-yellow-800/50 text-yellow-400',
    green:  'bg-green-950/40 border-green-800/50 text-green-400',
    blue:   'bg-blue-950/40 border-blue-800/50 text-blue-400',
    gray:   'bg-gray-800/60 border-gray-700/50 text-gray-400',
  };
  const lightColorMap = {
    red:    'bg-red-50 border-red-200 text-red-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    green:  'bg-green-50 border-green-200 text-green-700',
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    gray:   'bg-gray-100 border-gray-200 text-gray-600',
  };
  return (
    <div className={`flex-1 min-w-0 rounded-lg border px-3 py-2.5 dark:${colorMap[color] || colorMap.gray} ${lightColorMap[color] || lightColorMap.gray}`}>
      <div className="flex items-center gap-1.5">
        <span className="text-base leading-none">{icon}</span>
        <span className="text-lg font-bold tabular-nums leading-none">{count}</span>
      </div>
      <p className="text-xs mt-1 opacity-80 leading-tight">{label}</p>
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────
const BriefingCanvas = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [briefing, setBriefing] = useState(null);
  const [operations, setOperations] = useState({ summary: {}, shadow: {}, cases: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tc = {
    bg:       isDark ? 'bg-gray-950'      : 'bg-gray-50',
    card:     isDark ? 'bg-gray-900'      : 'bg-white',
    text:     isDark ? 'text-gray-100'    : 'text-gray-900',
    textSub:  isDark ? 'text-gray-400'    : 'text-gray-500',
    border:   isDark ? 'border-gray-700/60' : 'border-gray-200',
    divider:  isDark ? 'border-gray-800'  : 'border-gray-100',
  };

  useEffect(() => {
    const loadBriefing = async () => {
      try {
        setLoading(true);
        if (mockBriefing.schema_version !== '1.0') {
          throw new Error(`Nicht unterstützte Briefing-Schema-Version: ${mockBriefing.schema_version}`);
        }
        const expiresAt = new Date(mockBriefing.expires_at);
        // In development: skip expiry check so the mock always works
        // if (expiresAt < new Date()) throw new Error(`Briefing abgelaufen`);
        setBriefing(mockBriefing);

        const { data: { session } } = await masterSupabase.auth.getSession();
        if (session?.access_token) {
          const response = await fetch('/api/operations/context?limit=20', {
            headers: {
              Authorization: `Bearer ${session.access_token}`,
              'x-supabase-access-token': session.access_token,
            },
          });
          if (response.ok) setOperations(await response.json());
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadBriefing();
  }, []);

  const executeAction = ({ action, factId, source }) => {
    console.log(`[BriefingCanvas] Action: ${action} | Fact: ${factId} | Source: ${source}`);
    // Sprint 1B: Route to actual handlers
  };

  const downloadEvidence = async (record) => {
    if (!record.evidence_url) return;
    try {
      const { data: { session } } = await masterSupabase.auth.getSession();
      if (!session?.access_token) throw new Error('Keine aktive Clara-Sitzung');
      const response = await fetch(record.evidence_url, {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'x-supabase-access-token': session.access_token,
        },
      });
      if (!response.ok) throw new Error(`Originalnachweis nicht verfügbar (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `clara-evidence-${record.evidence_id || record.id}.eml`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (downloadError) {
      setError(downloadError.message);
    }
  };

  const renderFact = (fact) => {
    const registryEntry = briefingComponentRegistry[fact.component_id];
    if (!registryEntry) {
      return (
        <div key={fact.fact_id} className="p-4 rounded-xl border border-yellow-400/50 bg-yellow-50 dark:bg-yellow-950/30 text-yellow-800 dark:text-yellow-300 text-sm">
          <strong>Unbekannte Komponente:</strong> {fact.component_id}
        </div>
      );
    }
    if (registryEntry.capabilityId !== fact.capability_id) {
      return (
        <div key={fact.fact_id} className="p-4 rounded-xl border border-red-400/50 bg-red-50 dark:bg-red-950/30 text-red-800 dark:text-red-300 text-sm">
          <strong>Sicherheitsfehler:</strong> Capability-ID stimmt nicht überein.
        </div>
      );
    }
    const Component = registryEntry.component;
    return <Component key={fact.fact_id} fact={fact} executeAction={executeAction} />;
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={`min-h-screen ${tc.bg} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className={`text-sm ${tc.textSub}`}>Briefing wird geladen…</p>
        </div>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={`min-h-screen ${tc.bg} flex items-center justify-center p-4`}>
        <div className={`${tc.card} rounded-xl border border-red-300 dark:border-red-800 p-6 max-w-md w-full`}>
          <h2 className="text-base font-semibold text-red-600 dark:text-red-400 mb-1">Briefing konnte nicht geladen werden</h2>
          <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  // ── Empty ──────────────────────────────────────────────────────────────────
  if (!briefing?.facts?.length && !operations?.cases?.length) {
    return (
      <div className={`min-h-screen ${tc.bg} flex items-center justify-center p-4`}>
        <div className={`${tc.card} rounded-xl border ${tc.border} p-8 max-w-sm w-full text-center`}>
          <p className="text-5xl mb-4">☀️</p>
          <h2 className={`text-lg font-semibold ${tc.text} mb-2`}>Alles erledigt</h2>
          <p className={`text-sm ${tc.textSub}`}>Für heute gibt es keine offenen Punkte.</p>
        </div>
      </div>
    );
  }

  const summary = briefing.daily_summary;
  const generatedDate = new Date(briefing.generated_at);
  const expiresDate = new Date(briefing.expires_at);

  return (
    <div className={`min-h-screen ${tc.bg}`}>
      <div className="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">

        {/* ── Executive Header ──────────────────────────────────────────────── */}
        <div className={`rounded-2xl border ${tc.border} ${tc.card} overflow-hidden`}>
          {/* Begrüßung */}
          <div className="px-5 pt-5 pb-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className={`text-2xl font-bold ${tc.text} leading-tight`}>
                  🌅 Guten Morgen
                </h1>
                <p className={`text-sm ${tc.textSub} mt-0.5`}>
                  {generatedDate.toLocaleDateString('de-DE', {
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </p>
              </div>
              <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border ${tc.border} ${tc.textSub}`}>
                {(briefing.facts?.length || 0) + (operations.cases?.length || 0)} Punkte
              </span>
            </div>
          </div>

          {/* Tagesübersicht Cockpit */}
          {summary && (
            <>
              <div className={`border-t ${tc.divider} px-5 py-3`}>
                <p className={`text-xs uppercase tracking-wider font-semibold ${tc.textSub} mb-3`}>
                  Tagesübersicht
                </p>
                <div className="flex gap-2 flex-wrap">
                  {Number(operations.summary?.open_cases || 0) > 0 && (
                    <SummaryTile icon="●" count={operations.summary.open_cases} label="Clara-Fälle" color="blue" />
                  )}
                  {Number(operations.summary?.blocked_cases || 0) > 0 && (
                    <SummaryTile icon="!" count={operations.summary.blocked_cases} label="Zuordnung nötig" color="yellow" />
                  )}
                  {Number(operations.shadow?.decisions_required || 0) > 0 && (
                    <SummaryTile icon="✓" count={operations.shadow.decisions_required} label="Entscheidung" color="red" />
                  )}
                  {summary.critical_payments > 0 && (
                    <SummaryTile icon="🔴" count={summary.critical_payments} label="Krit. Zahlung" color="red" />
                  )}
                  {summary.index_reviews > 0 && (
                    <SummaryTile icon="🟡" count={summary.index_reviews} label="Indexmieten" color="yellow" />
                  )}
                  {summary.documents_ready > 0 && (
                    <SummaryTile icon="🟢" count={summary.documents_ready} label="Dok. bereit" color="green" />
                  )}
                  {summary.ai_suggestions > 0 && (
                    <SummaryTile icon="⚡" count={summary.ai_suggestions} label="KI-Vorschlag" color="blue" />
                  )}
                </div>
                {summary.portfolio_note && (
                  <p className={`text-xs ${tc.textSub} mt-3 italic`}>
                    📈 Portfolio: {summary.portfolio_note}
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Kanonische Clara-Fälle ─────────────────────────────────────────── */}
        {operations.cases?.length > 0 && (
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className={`text-sm font-semibold uppercase tracking-wider ${tc.textSub}`}>Operative Clara-Fälle</h2>
              <span className={`text-xs ${tc.textSub}`}>Quelle: gemeinsamer Case-Kern</span>
            </div>
            {operations.cases.map((record) => (
              <article key={record.id} className={`${tc.card} rounded-xl border ${tc.border} p-4`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className={`font-semibold ${tc.text}`}>{record.title}</h3>
                    <p className={`mt-1 text-sm ${tc.textSub}`}>{record.summary}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${record.blocker_code ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                    {record.blocker_code ? 'Klärung nötig' : record.state}
                  </span>
                </div>
                {record.blocker_detail && (
                  <p className="mt-3 rounded-md bg-yellow-50 p-2 text-xs text-yellow-800">
                    <strong>{record.blocker_code}:</strong> {record.blocker_detail}
                  </p>
                )}
                <div className={`mt-3 flex flex-wrap items-center gap-3 text-xs ${tc.textSub}`}>
                  <span>{record.evidence_metadata?.subject || record.event_type}</span>
                  <span>{new Date(record.evidence_received_at).toLocaleString('de-DE')}</span>
                  {record.evidence_url && (
                    <button type="button" onClick={() => downloadEvidence(record)} className="font-medium text-blue-600 hover:text-blue-800">
                      Original-E-Mail
                    </button>
                  )}
                </div>
              </article>
            ))}
          </section>
        )}

        {/* ── Bestehende Fact Cards ───────────────────────────────────────────── */}
        {briefing?.facts?.length > 0 && (
          <div className="space-y-4">
            {briefing.facts.map(fact => renderFact(fact))}
          </div>
        )}

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        <div className={`text-center text-xs ${tc.textSub} pb-4`}>
          <p>Briefing-ID: {briefing.briefing_id} · Schema v{briefing.schema_version}</p>
          <p className="mt-0.5">
            Gültig bis {expiresDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
          </p>
        </div>

      </div>
    </div>
  );
};

export default BriefingCanvas;
